import { cn } from "@/lib/utils";

export interface CrmCommunicationMemoryItem {
  label: string;
  value: string;
  evidence: string;
  confidence: number;
}

export interface CrmCommunicationMemoryDisplay {
  items: CrmCommunicationMemoryItem[];
  totalInteractions: number;
  hasMemory: boolean;
}

interface CrmCommunicationMemoryCardProps {
  memory: CrmCommunicationMemoryDisplay | null;
  title?: string;
  emptyDescription?: string;
  maxItems?: number;
  className?: string;
}

export function CrmCommunicationMemoryCard({
  memory,
  title = "Communication Memory",
  emptyDescription = "This contact does not have enough interaction history yet.",
  maxItems = 6,
  className,
}: CrmCommunicationMemoryCardProps) {
  const visibleItems = memory?.hasMemory
    ? memory.items.slice(0, Math.max(0, maxItems))
    : [];

  return (
    <section className={cn("rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4", className)}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">{title}</h3>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            {memory?.hasMemory
              ? `${memory.totalInteractions} tracked interactions inform these operator hints.`
              : emptyDescription}
          </p>
        </div>
      </div>
      {visibleItems.length > 0 ? (
        <div className="mt-3 space-y-2">
          {visibleItems.map((item, index) => (
            <CommunicationMemoryItemCard key={`${item.label}-${item.value}-${index}`} item={item} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function CommunicationMemoryItemCard({ item }: { item: CrmCommunicationMemoryItem }) {
  return (
    <div className="rounded-lg bg-[var(--color-background)] px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold uppercase text-[var(--color-muted-foreground)]">{item.label}</span>
        <span className="text-[10px] text-[var(--color-muted-foreground)]">
          {formatCommunicationMemoryConfidence(item.confidence)}
        </span>
      </div>
      <div className="mt-1 text-sm text-[var(--color-foreground)]">{item.value}</div>
      <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">{item.evidence}</div>
    </div>
  );
}

export function formatCommunicationMemoryConfidence(confidence: number) {
  if (!Number.isFinite(confidence)) return "0%";
  return `${Math.max(0, Math.min(100, Math.round(confidence * 100)))}%`;
}
